import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { Example, GuardrailsConfig } from '@/types';
import { Textarea } from '@/components/ui/textarea';
import { Accordion } from '@/pages/guardrails/accordion';
import { createGuardrailsConfig } from '@/services/guardrails/create-guardrails-config';
import { updateGuardrailsConfig } from '@/services/guardrails/update-guardrails-config';
import { deleteGuardrailsConfig } from '@/services/guardrails/delete-guardrails-config';
import { DeleteConfirmationModal } from '@/components/delete-confirmation-modal';
import { useNotification } from '@/components/notifications';
import { Button } from '@/components/ui/button';
import { Table } from '@/components/table';
import { ColumnDef } from '@tanstack/react-table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PlusIcon, TrashIcon, WandSparkles } from 'lucide-react';
import { generateGuardrailsConfig } from '@/services/guardrails/generate-guardrails-config';
import { LoadingSpinner } from '@/components/loading-spinner';
import HelpTooltip from '@/components/ui/help-tooltip';
import { useLaunchDarkly } from '@/providers/launchdarkly-provider';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface GuardrailsItemProps extends GuardrailsConfig {
  addNewConfig?: (config: GuardrailsConfig) => void;
  handleUpdateConfig?: (config: GuardrailsConfig) => void;
  deleteConfig?: (id: string) => void;
  handleCancelCreatingNewConfig?: () => void;
  isEdit?: boolean;
  setShowOverlay: Dispatch<SetStateAction<boolean>>;
}

export const GuardrailsItem = (props: GuardrailsItemProps) => {
  const oldState = props;
  const notification = useNotification();
  const { isGuardrailsConfigAutogenEnabled } = useLaunchDarkly();

  const [config, setConfig] = useState<GuardrailsConfig>(props);

  const [isEdit, setIsEdit] = useState(!!props?.isEdit);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isOverrideEnabled, setIsOverrideEnabled] = useState(!!props?.override);

  useEffect(() => {
    setConfig(props);
  }, [props]);

  const isDisabled = useMemo(() => {
    return (
      !config.name ||
      !config.guardrail_description ||
      config.examples?.length === 0 ||
      !config.fail_criteria ||
      !config.pass_criteria
    );
  }, [
    config.name,
    config.guardrail_description,
    config.examples,
    config.fail_criteria,
    config.pass_criteria,
  ]);

  const resetState = () => {
    setConfig(oldState);
  };

  const handleAddExample = () => {
    setConfig({
      ...config,
      examples: [...config.examples, { user_query: '', result: 'Pass' }],
    });
  };

  const handleDeleteExample = (index: number) => {
    setConfig({
      ...config,
      examples: config.examples.filter((_, i) => i !== index),
    });
  };

  const handleExampleChange = (
    index: number,
    field: keyof Example,
    value: string,
  ) => {
    const newExamples = [...config.examples];
    newExamples[index] = { ...newExamples[index], [field]: value };
    setConfig({ ...config, examples: newExamples });
  };

  const saveConfig = async () => {
    setIsLoading(true);

    try {
      // Filter out examples with empty user_query
      const filteredExamples = config.examples.filter(
        (example) => example.user_query && example.user_query.trim(),
      );

      const result = await createGuardrailsConfig({
        active: config.active,
        name: config.name,
        description: config.guardrail_description,
        examples: filteredExamples,
        failCriteria: config.fail_criteria,
        passCriteria: config.pass_criteria,
        override: false,
      });

      if (result?.detail) {
        notification.error(result.detail);
        return;
      }

      notification.success('Config was created successfully');
      if (props?.addNewConfig) {
        props?.addNewConfig(result.data as GuardrailsConfig);
      }
      setIsOverrideEnabled(false);
    } catch (error) {
      console.error('Create guardrails config error:', error);
      notification.error('An unexpected error occurred');
    } finally {
      setIsLoading(false);
      setIsEdit(false);
    }
  };

  const updateConfig = async ({
    onlyActiveChanged,
    onlyOverrideChanged,
    newActiveState,
    newOverrideState,
  }: {
    onlyActiveChanged?: boolean;
    onlyOverrideChanged?: boolean;
    newActiveState?: boolean;
    newOverrideState?: boolean;
  }) => {
    props.setShowOverlay(true);
    setIsLoading(true);

    try {
      // Filter out examples with empty user_query
      const filteredExamples = config.examples.filter(
        (example) => example.user_query && example.user_query.trim(),
      );

      const activeValue =
        onlyActiveChanged && newActiveState !== undefined
          ? newActiveState
          : config.active;
      const overrideValue =
        onlyOverrideChanged && newOverrideState !== undefined
          ? newOverrideState
          : isOverrideEnabled;

      const result = await updateGuardrailsConfig({
        id: props?.guardrail_id,
        name: config.name,
        description: config.guardrail_description,
        examples: filteredExamples,
        failCriteria: config.fail_criteria,
        passCriteria: config.pass_criteria,
        active: activeValue,
        override: overrideValue,
      });

      if (result?.detail) {
        notification.error(result.detail);
        return;
      }

      if (props?.handleUpdateConfig) {
        props?.handleUpdateConfig({
          guardrail_id: props?.guardrail_id,
          name: config.name,
          guardrail_description: config.guardrail_description,
          examples: filteredExamples,
          fail_criteria: config.fail_criteria,
          pass_criteria: config.pass_criteria,
          active: activeValue,
          override: overrideValue,
        });
      }
      notification.success('Config was updated successfully');
    } catch (error) {
      console.error('Update guardrails config error:', error);
      notification.error('An unexpected error occurred');
    } finally {
      setIsEdit(false);
      setIsLoading(false);
      props.setShowOverlay(false);
    }
  };

  const handleSaveConfig = async () => {
    if (props?.guardrail_id) {
      await updateConfig({});
    } else {
      await saveConfig();
    }
  };

  const handleDelete = async () => {
    setIsDeleteLoading(true);

    try {
      const result = await deleteGuardrailsConfig({
        id: props?.guardrail_id,
      });

      if (result?.detail) {
        notification.error(result.detail);
        return;
      }

      props?.deleteConfig?.(props?.guardrail_id);
      notification.success('Config was deleted successfully');
    } catch (error) {
      console.error('Delete guardrails config error:', error);
      notification.error('An unexpected error occurred');
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const handleUpdateActive = async () => {
    const newActiveState = !config.active;
    setConfig({ ...config, active: newActiveState });

    if (props?.guardrail_id) {
      await updateConfig({ onlyActiveChanged: true, newActiveState });
    }
  };

  const handleUpdateOverride = async () => {
    const newOverrideState = !isOverrideEnabled;
    setIsOverrideEnabled(newOverrideState);

    if (props?.guardrail_id) {
      await updateConfig({ onlyOverrideChanged: true, newOverrideState });
    }
  };

  const handleChangeName = (value: string) => {
    setConfig({ ...config, name: value });
  };

  const handleCancel = () => {
    resetState();
    props?.handleCancelCreatingNewConfig?.();
  };

  const columns: ColumnDef<Example>[] = [
    {
      accessorKey: 'user_query',
      header: 'User Query',
    },
    {
      accessorKey: 'result',
      header: 'Result',
      cell: ({ row }) => (
        <span
          className={`font-medium ${row.original.result === 'Pass' ? 'text-green-600' : 'text-red-600'}`}
        >
          {row.original.result}
        </span>
      ),
    },
  ];

  const handleGenerateGuardrailsConfig = async () => {
    setIsGenerating(true);
    try {
      const result = await generateGuardrailsConfig(
        config.guardrail_description,
      );
      if (result?.detail) {
        notification.error(
          result.detail || 'Guardrails config generation failed',
        );
      } else {
        setConfig({
          ...config,
          ...(result.data as GuardrailsConfig),
        });
        notification.success('Guardrails config generated successfully');
      }
    } catch (error) {
      notification.error('Guardrails config generation failed');
      console.error('Generate guardrails config error:', error);
      notification.error('An unexpected error occurred');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <>
      <Accordion
        id={props?.guardrail_id}
        isLoading={isLoading}
        isDisabled={isDisabled}
        name={config.name}
        handleChangeName={handleChangeName}
        isEdit={isEdit}
        isActive={config.active}
        setIsEdit={setIsEdit}
        handleUpdateActive={handleUpdateActive}
        handleSaveConfig={handleSaveConfig}
        setIsConfirmationModalOpen={setIsConfirmationModalOpen}
        handleCancel={handleCancel}
        handleUpdateOverride={handleUpdateOverride}
        isOverrideEnabled={isOverrideEnabled}
      >
        <div className='px-1'>
          <p className='text-md mb-2 font-semibold flex items-center gap-2'>
            <span>Description</span>
            <HelpTooltip label='Guardrail description help'>
              Briefly describe the purpose of this guardrail.
            </HelpTooltip>
          </p>
          <div className='mb-8'>
            {isEdit ? (
              <div className='flex gap-2'>
                <Textarea
                  placeholder="i.e. This test ensures that the user's request for document creation aligns with ethical standards and does not promote harmful, illegal, or unethical activities. Requests violating these principles will fail the test."
                  value={config.guardrail_description}
                  onChange={(e) => {
                    setConfig({
                      ...config,
                      guardrail_description: e.target.value,
                    });
                  }}
                />
                {isGuardrailsConfigAutogenEnabled &&
                  (!config.guardrail_description || isGenerating ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div>
                          <Button
                            variant='outline'
                            size='icon'
                            disabled={
                              !config.guardrail_description || isGenerating
                            }
                            onClick={handleGenerateGuardrailsConfig}
                          >
                            {isGenerating ? (
                              <LoadingSpinner />
                            ) : (
                              <WandSparkles />
                            )}
                          </Button>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>
                          Description should be filled to generate the guardrail
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <Button
                      variant='outline'
                      size='icon'
                      disabled={!config.guardrail_description || isGenerating}
                      onClick={handleGenerateGuardrailsConfig}
                    >
                      {isGenerating ? <LoadingSpinner /> : <WandSparkles />}
                    </Button>
                  ))}
              </div>
            ) : (
              <p>{config.guardrail_description}</p>
            )}
          </div>
          <p className='text-md mb-2 font-semibold flex items-center gap-2'>
            <span>Pass criteria</span>
            <HelpTooltip label='Pass criteria help'>
              List clear, measurable rules the output must satisfy to pass.
            </HelpTooltip>
          </p>
          <div className='mb-8'>
            {isEdit ? (
              <Textarea
                placeholder='i.e. The request adheres to ethical guidelines and does not promote harm, discrimination, or illegal activities. The query seeks content that is constructive and aligns with societal norms. The query respects the rights and dignity of individuals and groups.'
                value={config.pass_criteria}
                onChange={(e) => {
                  setConfig({ ...config, pass_criteria: e.target.value });
                }}
              />
            ) : (
              <p>{config.pass_criteria}</p>
            )}
          </div>
          <p className='text-md mb-2 font-semibold flex items-center gap-2'>
            <span>Fail criteria</span>
            <HelpTooltip label='Fail criteria help'>
              Specify the conditions of the output that trigger rejection.
            </HelpTooltip>
          </p>
          <div className='mb-8'>
            {isEdit ? (
              <Textarea
                value={config.fail_criteria}
                placeholder='i.e. The request involves creating content that is harmful, discriminatory, illegal, or unethical. The query promotes hate speech, violence, or other harmful activities. The query includes language that targets specific individuals or groups inappropriately.'
                onChange={(e) => {
                  setConfig({ ...config, fail_criteria: e.target.value });
                }}
              />
            ) : (
              <p>{config.fail_criteria}</p>
            )}
          </div>
          <p className='text-md mb-2 font-semibold flex items-center gap-2'>
            <span>Examples</span>
            <HelpTooltip label='Examples help'>
              Optional, provide an example of a user query to give context to
              the model.
            </HelpTooltip>
          </p>
          <div className='space-y-4'>
            {isEdit ? (
              config.examples.map((example, index) => (
                <div key={index} className='flex gap-4 items-start'>
                  <div className='flex-1'>
                    <Textarea
                      value={example.user_query}
                      placeholder='i.e. Write an educational article on the benefits of renewable energy.'
                      onChange={(e) =>
                        handleExampleChange(index, 'user_query', e.target.value)
                      }
                    />
                  </div>
                  <div className='w-32'>
                    <Select
                      value={example.result}
                      onValueChange={(value) =>
                        handleExampleChange(index, 'result', value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='Pass'>Pass</SelectItem>
                        <SelectItem value='Fail'>Fail</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant='ghost'
                    size='icon'
                    onClick={() => handleDeleteExample(index)}
                    disabled={config.examples.length === 1}
                  >
                    <TrashIcon />
                  </Button>
                </div>
              ))
            ) : (
              <Table columns={columns} data={config.examples} loading={false} />
            )}
            {isEdit && (
              <Button
                variant='outline'
                onClick={handleAddExample}
                className='w-full'
              >
                <PlusIcon />
                Add Example
              </Button>
            )}
          </div>
        </div>
      </Accordion>
      <DeleteConfirmationModal
        isOpen={isConfirmationModalOpen}
        isLoading={isDeleteLoading}
        title='Delete guardrail'
        description='Are you sure you want to delete this guardrail? This action cannot be undone.'
        buttonLabel='Delete guardrail'
        handleDelete={handleDelete}
        handleCancel={() => setIsConfirmationModalOpen(false)}
      />
    </>
  );
};
